import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  CheckCircle
} from "lucide-react";

const serviceAreas = [
  {
    region: "Manhattan",
    areas: ["Midtown", "Upper East Side", "Upper West Side", "Harlem", "Financial District", "Chelsea", "SoHo", "Tribeca"]
  },
  {
    region: "Brooklyn",
    areas: ["Williamsburg", "Park Slope", "Brooklyn Heights", "DUMBO", "Bay Ridge", "Flatbush", "Bushwick", "Red Hook"]
  },
  {
    region: "Queens",
    areas: ["Astoria", "Long Island City", "Flushing", "Forest Hills", "Jamaica", "Bayside", "Jackson Heights", "Woodside"]
  },
  {
    region: "Bronx",
    areas: ["Riverdale", "Fordham", "Pelham Bay", "Throgs Neck", "Mott Haven", "Hunts Point", "Wakefield", "Morris Park"]
  },
  {
    region: "Staten Island",
    areas: ["St. George", "Tottenville", "Great Kills", "New Dorp", "Stapleton", "Port Richmond", "West Brighton", "Eltingville"]
  },
  {
    region: "Long Island",
    areas: ["Garden City", "Great Neck", "Manhasset", "Roslyn", "Jericho", "Syosset", "Oyster Bay", "Huntington"]
  },
  {
    region: "Westchester",
    areas: ["White Plains", "Yonkers", "New Rochelle", "Scarsdale", "Rye", "Mamaroneck", "Tarrytown", "Mount Vernon"]
  },
  {
    region: "New Jersey",
    areas: ["Jersey City", "Hoboken", "Newark", "Fort Lee", "Edgewater", "Weehawken", "West New York", "Union City"]
  }
];

export default function ServiceAreasPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="areas-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1534430480872-3498386e7856?q=80&w=2070"
            alt="New York City"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sky-400 font-bold uppercase tracking-widest text-sm mb-4 block"
            >
              Service Areas
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="heading-primary text-white mb-6"
            >
              Serving the Greater New York Metropolitan Area
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-300"
            >
              From Manhattan to Long Island, we provide professional pressure washing 
              services throughout the tri-state area.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Service Areas Grid */}
      <section className="section-padding bg-white" data-testid="areas-grid">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="heading-secondary text-slate-900 mb-6">
              Areas We Serve
            </h2>
            <p className="text-slate-600">
              BuyVistas proudly serves residential and commercial customers throughout 
              these neighborhoods and surrounding areas. Don't see your location? Contact 
              us to check availability.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceAreas.map((area, index) => (
              <motion.div
                key={area.region}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-50 p-6 border border-slate-200"
                data-testid={`area-${area.region.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-sky-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {area.region}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {area.areas.map((neighborhood) => (
                    <li key={neighborhood} className="flex items-center gap-2 text-slate-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-sky-600 flex-shrink-0" />
                      {neighborhood}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-slate-50" data-testid="areas-map">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sky-600 font-bold uppercase tracking-widest text-sm mb-4 block">
                Coverage Area
              </span>
              <h2 className="heading-secondary text-slate-900 mb-6">
                Comprehensive Coverage Throughout the Region
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Our team of professionals travels throughout the New York metropolitan 
                area to provide top-quality pressure washing services. Whether you're in 
                the heart of Manhattan or the suburbs of Long Island, we're ready to serve you.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">
                    <strong>Same-day service</strong> available in most areas
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">
                    <strong>No travel fees</strong> within our primary service area
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sky-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">
                    <strong>Emergency services</strong> for urgent cleaning needs
                  </p>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6">
                Not sure if we serve your area? Give us a call and we'll be happy to help.
              </p>
              
              <a 
                href="tel:+1-888-435-4674"
                className="btn-primary inline-block"
                data-testid="areas-call-btn"
              >
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call +1-888-435-4674
                </span>
              </a>
            </div>
            
            <div className="h-[500px] bg-slate-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304605!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1703000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BuyVistas Service Area Map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16" data-testid="areas-cta">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-secondary text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-slate-300 text-lg">
                Contact us today for a free quote on your pressure washing project.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="areas-quote-btn">
                <span>Get Free Quote</span>
              </Link>
              <a href="tel:+1-888-435-4674" className="btn-secondary" data-testid="areas-phone-btn">
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  +1-888-435-4674
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
